/**
 * Adapter layer to integrate @chargebee/code-sample-generator package
 *
 * Bridges API Explorer's data structures (ApiAttribute[], Endpoint, etc.)
 * to the code-sample-generator's generate() API.
 *
 * Conversion logic migrated from Convertor.ts to eliminate JSON round-trip.
 */

import { createCodeGenerator } from '@chargebee/code-sample-generator/browser';
import type {
  ApiAttribute,
  Endpoint,
  EndpointAttributes,
} from '@context/openapi/types';
import type { RequestSampleOperation } from './requestSample/Operation';

// ---------------------------------------------------------------------------
// Attribute flattening — converts Explorer's ApiAttribute[] → flat params
// with bracket notation keys (e.g., "billing_address[city]", "items[id][0]")
// ---------------------------------------------------------------------------

/**
 * Format a parameter key with bracket notation.
 * Converts dot notation to bracket notation for API compatibility.
 *
 * @example
 * formatParamKey('first_name') → 'first_name'
 * formatParamKey('first_name', 'is') → 'first_name[is]'
 * formatParamKey('billing_address.city') → 'billing_address[city]'
 * formatParamKey('items[0].price_id') → 'items[price_id][0]'
 * formatParamKey('invoice.status', 'is_not') → 'invoice[status][is_not]'
 */
function formatParamKey(key: string, filter?: string): string {
  const keys = key.split('.');
  if (keys.length === 1) {
    return filter ? `${keys[0]}[${filter}]` : keys[0];
  }
  const arrayIndex = keys[0].match(/\[\d+\]/g);
  const parentKey = arrayIndex ? keys[0].replace(/\[\d+\]/g, '') : keys[0];
  const paramKey = arrayIndex
    ? `${parentKey}[${keys[1]}]${arrayIndex[0]}`
    : `${parentKey}[${keys[1]}]`;
  return filter ? `${paramKey}[${filter}]` : paramKey;
}

/**
 * Format a value based on its type.
 * Converts NUMBER types to actual numbers, others to strings.
 */
function formatValue(value: string | number, type: string): string | number {
  return type === 'NUMBER' ? Number(value) : String(value);
}

/**
 * Parse object-type attributes (e.g., billing_address with nested fields).
 */
function parseObjectParams(
  attribute: ApiAttribute,
  params: Record<string, unknown>
): void {
  if (attribute.child.length === 0 && attribute.value) {
    params[attribute.key] = attribute.value;
    return;
  }
  for (const child of attribute.child) {
    if (
      child.value !== undefined &&
      child.value !== null &&
      child.value !== '' &&
      child.key
    ) {
      const paramKey = formatParamKey(child.key, child.filter);
      const paramValue = formatValue(child.value, child.type);
      params[paramKey] = paramValue;
    }
  }
}

/**
 * Parse array-type attributes (e.g., subscription_items with indexed items).
 */
function parseArrayParams(
  attribute: ApiAttribute,
  params: Record<string, unknown>
): void {
  for (const child of attribute.child) {
    if (
      child.value !== undefined &&
      child.value !== null &&
      child.value !== '' &&
      child.key
    ) {
      const paramKey = formatParamKey(child.key, child.filter);
      const paramValue = formatValue(child.value, child.type);
      params[paramKey] = paramValue;
    }
  }
}

/**
 * Parse simple (non-nested) attributes.
 */
function parseSimpleAttribute(
  attr: ApiAttribute,
  params: Record<string, unknown>
): void {
  if (
    attr.value !== undefined &&
    attr.value !== null &&
    attr.value !== '' &&
    attr.key
  ) {
    const paramKey = formatParamKey(attr.key, attr.filter);
    const paramValue = formatValue(attr.value, attr.type);
    params[paramKey] = paramValue;
  }
}

/**
 * Flatten ApiAttribute[] to a flat params object with bracket notation keys.
 *
 * This function converts the Explorer's nested ApiAttribute tree into a flat
 * key-value map that the code-sample-generator's ir-builder can process.
 *
 * @example
 * Input: [{ key: 'billing_address', type: 'OBJECT', child: [
 *   { key: 'billing_address.city', value: 'NYC', type: 'STRING' }
 * ]}]
 *
 * Output: { 'billing_address[city]': 'NYC' }
 */
export function flattenAttributesToBracketNotation(
  attributes: EndpointAttributes
): Record<string, unknown> {
  const params: Record<string, unknown> = {};

  const allAttributes = [
    ...(attributes.queryParams || []),
    ...(attributes.params || []),
  ];

  for (const attr of allAttributes) {
    switch (attr.type) {
      case 'OBJECT':
        parseObjectParams(attr, params);
        break;
      case 'ARRAY':
        parseArrayParams(attr, params);
        break;
      default:
        parseSimpleAttribute(attr, params);
    }
  }

  return params;
}

// ---------------------------------------------------------------------------
// Singleton code generator
// ---------------------------------------------------------------------------

let codeGeneratorInstance: ReturnType<typeof createCodeGenerator> | null = null;
let lastSiteName: string | undefined;
let lastApiKey: string | undefined;

/**
 * Get or create the singleton code generator.
 * Recreates when site/apiKey changes.
 *
 * Note: specsBaseUrl is intentionally omitted — the browser generator uses
 * bundled operation descriptors (no network fetch required).
 */
function getCodeGenerator(
  siteName?: string,
  apiKey?: string
): ReturnType<typeof createCodeGenerator> {
  if (
    !codeGeneratorInstance ||
    lastSiteName !== siteName ||
    lastApiKey !== apiKey
  ) {
    codeGeneratorInstance = createCodeGenerator({
      apiVersion: 'v2',
      pcVersion: 'v2',
      site: siteName || 'your-site',
      apiKey: apiKey || 'test_api_key',
      cache: 'memory',
    });
    lastSiteName = siteName;
    lastApiKey = apiKey;
  }
  return codeGeneratorInstance;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Pre-fetch operation spec to warm up the in-memory cache.
 * Fire-and-forget — errors are silently ignored.
 */
export async function prefetchOperationSpec(
  operationId: string,
  _domainName: string,
  siteName?: string,
  apiKey?: string
): Promise<void> {
  try {
    const codegen = getCodeGenerator(siteName, apiKey);
    await codegen.prefetch(operationId);
  } catch {
    // Prefetch is best-effort
  }
}

/**
 * Generate a code sample for the given operation + language.
 *
 * Flattens the Explorer's attribute trees into a flat params object
 * with bracket notation keys, includes path parameters, and delegates
 * to the code-sample-generator.
 */
export async function generateCodeSample(
  operation: RequestSampleOperation,
  lang: string,
  _domainName: string,
  siteName?: string,
  apiKey?: string,
  endpoint?: Endpoint,
  attributes?: EndpointAttributes
): Promise<string> {
  if (!endpoint || !attributes) {
    throw new Error('Endpoint and attributes are required');
  }

  const codegen = getCodeGenerator(siteName, apiKey);

  // Flatten body and query params to bracket notation (no JSON round-trip)
  const bodyAndQueryParams = flattenAttributesToBracketNotation(attributes);

  // Include path parameters (e.g. customer_id, item_id)
  const pathParams: Record<string, string> = {};
  if (attributes.pathParameters) {
    for (const param of attributes.pathParameters) {
      if (param.value) {
        pathParams[param.key] = String(param.value);
      }
    }
  }

  // Merge all params — the generator's buildIR() sorts them into
  // path vs body vs query based on the operation descriptor.
  const params = { ...pathParams, ...bodyAndQueryParams };

  const result = await codegen.generate({
    language: lang,
    operation: operation.id,
    request: { params },
    site: siteName || 'your-site',
    apiKey: apiKey || 'test_api_key',
  });

  return result.code;
}

/**
 * Clear the code generator cache.
 */
export function clearCodeGeneratorCache(): void {
  codeGeneratorInstance?.clearCache();
}

/**
 * Reset the code generator instance (useful for testing).
 */
export function resetCodeGenerator(): void {
  codeGeneratorInstance = null;
}
